// Project and document templates (SuiteModel/Templates.swift, review B13): what a new project starts with, the built-in
// document templates, and the writer's own (the top-level items of a Templates folder in Research & Notes). The writes
// (`apply`, `newDocument(from:)`, `copyDocument`, `saveAsTemplate`) are in edits.ts.
import type { ProjectType } from '../contract.ts';
import type { ScriptFormat } from '../scriptFormat.ts';
import { CharacterStudy } from '../edit/storeEditor.ts';
import type { ArrangementTreeNode } from '../model/arrangement.ts';

/** `ProjectTemplate.Item`: a document or folder a template creates. */
export interface TemplateItem {
  title: string;
  /** A folder (always, when it has children). */
  folder: boolean;
  /** A Section type option by name ("Part", "Chapter", "Scene"). */
  sectionType?: string;
  text: string;
  notes: string;
  children: TemplateItem[];
}

/** `Item.init`: a folder whenever it has children. */
export function item(title: string, o: { folder?: boolean; sectionType?: string; text?: string; notes?: string; children?: TemplateItem[] } = {}): TemplateItem {
  const children = o.children ?? [];
  return {
    title, folder: (o.folder ?? false) || children.length > 0, ...(o.sectionType !== undefined ? { sectionType: o.sectionType } : {}),
    text: o.text ?? '', notes: o.notes ?? '', children,
  };
}

export interface ProjectTemplate {
  id: string;
  name: string;
  summary: string;
  type: ProjectType;
  /** `scriptFormat`: a stage format makes the screenplay a play ('screen' when absent). */
  scriptFormat?: ScriptFormat;
  /** `musical`: a play that's a musical. */
  musical?: boolean;
  manuscript: TemplateItem[];
  research: TemplateItem[];
  manuscriptWords?: number;
  /** Project compile presets (id → YAML). */
  presets: Record<string, string>;
}

/** `ProjectTemplate.firstScene`: the path (indices) to the first leaf of the manuscript. */
export function firstScene(items: TemplateItem[]): number[] | null {
  for (let i = 0; i < items.length; i++) {
    if (!items[i].folder) return [i];
    const path = firstScene(items[i].children);
    if (path) return [i, ...path];
  }
  return null;
}

const chapterOfScenes = (title: string, scene = 'Scene One') => item(title, { sectionType: 'Chapter', children: [item(scene, { sectionType: 'Scene' })] });

/** `ProjectTemplate.all`, verbatim. */
export const PROJECT_TEMPLATES: readonly ProjectTemplate[] = [
  {
    id: 'novel', name: 'Novel', summary: 'Chapters of scenes, aiming at 80,000 words.', type: 'manuscript',
    manuscript: [chapterOfScenes('Chapter One')],
    research: [item('Plot', { notes: 'What happens, in a sentence per chapter.' }), item('Settings', { folder: true })],
    manuscriptWords: 80_000, presets: {},
  },
  {
    id: 'novella', name: 'Novella', summary: 'A shorter novel: chapters of scenes, aiming at 30,000 words.', type: 'manuscript',
    manuscript: [chapterOfScenes('Chapter One')], research: [], manuscriptWords: 30_000, presets: {},
  },
  {
    id: 'collection', name: 'Short-story collection',
    summary: 'Each story a folder, titled by its own name when compiled, with a manuscript preset that does so.', type: 'manuscript',
    manuscript: [chapterOfScenes('First Story')],
    research: [item('Story ideas', { notes: 'One line per story you might write.' })],
    manuscriptWords: 60_000,
    presets: {
      'collection-manuscript': 'name: Collection manuscript\ndescription: Standard manuscript format with each story headed by its title alone.\ninherits: shunn-docx\nrender:\n  chapterHeading: ["{title}"]',
      'collection-ebook': 'name: Collection ebook\ndescription: The collection as an EPUB, each story headed by its title.\ninherits: ebook\nrender:\n  chapterHeading: ["{title}"]',
    },
  },
  {
    id: 'memoir', name: 'Memoir', summary: 'Chapters of remembered scenes, with folders for people and a timeline.', type: 'manuscript',
    manuscript: [chapterOfScenes('Chapter One')],
    research: [item('People', { folder: true }), item('Timeline', { notes: 'Dates, places and what happened, in order.' }), item('Letters and records', { folder: true })],
    manuscriptWords: 70_000, presets: {},
  },
  {
    id: 'nonfiction', name: 'Nonfiction', summary: 'Parts, chapters and sections, with folders for sources and the outline.', type: 'manuscript',
    manuscript: [item('Part One', { sectionType: 'Part', children: [chapterOfScenes('Chapter One', 'Introduction')] })],
    research: [item('Outline', { notes: 'Each chapter\'s argument in a sentence or two.' }), item('Sources', { folder: true })],
    manuscriptWords: 60_000, presets: {},
  },
  {
    id: 'journal', name: 'Journal',
    summary: 'A blog or newsletter: each document a post, newest at the top, with the opening and sign-off every post shares.', type: 'journal',
    manuscript: [item('First Post')], research: [item('Ideas', { notes: 'Posts you might write, one to a line.' })], presets: {},
  },
  {
    id: 'screenplay', name: 'Screenplay', summary: 'Three acts, opening on FADE IN:.', type: 'screenplay',
    manuscript: [item('Act One', { children: [item('Opening', { text: 'FADE IN:\n\n' })] }), item('Act Two', { folder: true }), item('Act Three', { folder: true })],
    research: [], presets: {},
  },
  {
    id: 'stage-play', name: 'Stage play', summary: 'Acts of scenes in play manuscript format, with a cast of characters and a setting page.',
    type: 'screenplay', scriptFormat: 'stage-us',
    manuscript: [item('Act One', { children: [item('Scene One')] }), item('Act Two', { folder: true })],
    research: [], presets: {},
  },
  {
    id: 'musical', name: 'Musical',
    summary: 'A play with songs: acts of scenes in play manuscript format, songs numbered through the show, and a page of the musical numbers.',
    type: 'screenplay', scriptFormat: 'stage-us', musical: true,
    manuscript: [item('Act One', { children: [item('Scene One', { text: 'Song: Opening\n\n' })] }), item('Act Two', { folder: true })],
    research: [], presets: {},
  },
  {
    id: 'sitcom', name: 'Sitcom',
    summary: 'A multi-camera episode: a cold open, two acts and a tag, scenes lettered A, B, C, action in capitals and dialogue double-spaced.',
    type: 'screenplay', scriptFormat: 'multicam',
    manuscript: [item('Cold Open', { children: [item('Scene A')] }), item('Act One', { children: [item('Scene B')] }),
      item('Act Two', { children: [item('Scene C')] }), item('Tag', { children: [item('Scene D')] })],
    research: [], presets: {},
  },
  {
    id: 'radio-play', name: 'Radio play',
    summary: "A play for the ear in the BBC's scene style: numbered scenes, sound and music cues, and a cast list.",
    type: 'screenplay', scriptFormat: 'radio',
    manuscript: [item('Scene 1'), item('Scene 2')],
    research: [], presets: {},
  },
  {
    id: 'blank', name: 'Blank', summary: 'One empty scene and nothing else.', type: 'manuscript',
    manuscript: [item('Untitled')], research: [], presets: {},
  },
];

export const projectTemplate = (id: string) => PROJECT_TEMPLATES.find((t) => t.id === id) ?? null;

// MARK: Document templates

/** `DocumentTemplates`: fragment metadata `document-template: folder` marks the Templates folder. */
export const DocumentTemplates = {
  field: 'document-template',
  folderMarker: 'folder',
  folderTitle: 'Templates',
  placePrompts: ['Where it is', 'What it looks like', 'Sounds and smells', 'Who\'s there', 'History', 'What it means to the story'] as readonly string[],
  researchPrompts: ['Source', 'Date', 'Key points', 'Quotes to use', 'Follow up'] as readonly string[],
  prompts: (labels: readonly string[]) => labels.map((l) => `**${l}:** `).join('\n\n'),
};

export interface BuiltInTemplate {
  id: string;
  name: string;
  item: TemplateItem;
  /** Where it belongs: the manuscript (scenes and chapters) or research and notes. */
  forManuscript: boolean;
  /** A journal's post template (offered only in a journal's posts, where Scene and Chapter aren't). */
  forJournal?: boolean;
}

/** `BuiltIn.isOffered(journal:manuscript:)`. */
export const templateOffered = (t: BuiltInTemplate, journal: boolean, manuscript: boolean) =>
  t.forManuscript === manuscript && (!manuscript || (t.forJournal ?? false) === journal);

/** `DocumentTemplates.builtIns`. */
export const BUILT_IN_TEMPLATES: readonly BuiltInTemplate[] = [
  { id: 'scene', name: 'Scene', item: item('Untitled scene', { notes: 'Goal: \nConflict: \nOutcome: \nWhy it matters: ' }), forManuscript: true },
  { id: 'chapter', name: 'Chapter', item: chapterOfScenes('New Chapter'), forManuscript: true },
  { id: 'character', name: 'Character', item: item('New character', { text: CharacterStudy.template }), forManuscript: false },
  { id: 'place', name: 'Place', item: item('New place', { text: DocumentTemplates.prompts(DocumentTemplates.placePrompts) }), forManuscript: false },
  { id: 'research', name: 'Research note', item: item('New note', { text: DocumentTemplates.prompts(DocumentTemplates.researchPrompts) }), forManuscript: false },
  {
    id: 'post-in-sections', name: 'Post in sections', forManuscript: true, forJournal: true,
    // A document with sections under it, not a folder: a post.
    item: {
      ...item('Untitled', { children: [
        item('Opening', { notes: 'What brings the reader in: the moment, the question, the thing you noticed.' }),
        item('Middle', { notes: 'What you have to say about it.' }),
        item('Close', { notes: 'Where it leaves the reader.' }),
      ] }),
      folder: false,
    },
  },
];

/** `builtInTemplates`: the built-ins that suit where a new document goes (scenes and chapters in the manuscript or an
 *  alternate; characters, places and notes elsewhere). */
export const builtInTemplatesFor = (role: string | null | undefined, journal = false) =>
  BUILT_IN_TEMPLATES.filter((t) => templateOffered(t, journal, role === 'manuscript' || role === 'alternate'));

/** Where a new document goes: after the selection, or inside it if it's a folder (the top level with nothing selected). */
export function newDocumentPlacement(selected: { id: string; isFolder: boolean } | null): { after: string } | { inside: string | null } {
  if (!selected) return { inside: null };
  return selected.isFolder ? { inside: selected.id } : { after: selected.id };
}

/** Metadata keys a copy never carries (the markers of special pages). */
// A copy is never the same post: no number or publish date of the original's.
export const SKIPPED_ON_COPY: readonly string[] = [DocumentTemplates.field, CharacterStudy.field, 'book-matter', 'post-number', 'publish-date'];

/** The Templates folder among Research & Notes' top-level nodes: the first (in reading order) whose document is marked. */
export function findTemplatesFolder(research: ArrangementTreeNode[], metadataOf: (fragmentID: string) => Record<string, { type: string; value: unknown }> | undefined):
  ArrangementTreeNode | null {
  return research.find((t) => {
    const id = t.node.fragmentID;
    const v = id !== undefined ? metadataOf(id)?.[DocumentTemplates.field] : undefined;
    return v?.type === 'text' && v.value === DocumentTemplates.folderMarker;
  }) ?? null;
}
